/**
 * ConnectivityBanner (RESILIENCE Tier 1) — turns a backend/network blip from a
 * silent, stuck-forever blank screen (the 2026-06-18 incident) into a visible,
 * self-healing "Reconnecting…" bar.
 *
 * How it works:
 *  - A failed data request (network/timeout error) OR the browser going offline
 *    flips us into "degraded" and shows the bar.
 *  - While degraded we ping Supabase every few seconds; the moment it answers we
 *    clear the bar AND invalidate every query so the screens repopulate on their
 *    own — no manual reload, no hard refresh.
 *  - Real errors (a 400, a permission denial) do NOT trip it; only connectivity.
 *
 * Pairs with the request timeout in supabase.ts (so a wedged request errors
 * instead of hanging, which is what lets this detector fire at all).
 */
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

const HEALTH_URL = "https://trfusfityhwecvglnfhm.supabase.co/auth/v1/health";

/** Connectivity/backend-unavailable failures only — NOT a 400/permission error.
 * Covers hard network drops (Failed to fetch / timeout / offline) AND backend
 * "service unavailable" responses (the 503/502/504 storm we saw 2026-06-18, when
 * Supabase shed load — those come back as HTTP responses, not fetch failures). */
function isConnectivityError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: string; message?: string; status?: number; code?: string | number };
  if (typeof e.status === "number" && e.status >= 500) return true;
  const code = String(e.code ?? "");
  if (/^5\d\d$/.test(code)) return true;
  const m = (e.message ?? "").toLowerCase();
  return (
    e.name === "TypeError" || e.name === "AbortError" || e.name === "TimeoutError" ||
    m.includes("failed to fetch") || m.includes("timed out") || m.includes("timeout") ||
    m.includes("networkerror") || m.includes("load failed") ||
    m.includes("503") || m.includes("502") || m.includes("504") ||
    m.includes("service unavailable") || m.includes("bad gateway") || m.includes("gateway")
  );
}

export function ConnectivityBanner() {
  const qc = useQueryClient();
  const [degraded, setDegraded] = useState(false);

  // Ping Supabase; on success, clear the bar and refetch everything that's mounted.
  const tryRecover = useCallback(async (): Promise<boolean> => {
    try {
      const r = await fetch(HEALTH_URL, { cache: "no-store" });
      if (r.ok || r.status === 401) {
        setDegraded(false);
        qc.invalidateQueries(); // refetch all active queries → data repopulates
        return true;
      }
    } catch {
      /* still unreachable */
    }
    return false;
  }, [qc]);

  // While degraded, poll for recovery every 5s.
  useEffect(() => {
    if (!degraded) return;
    const id = window.setInterval(() => { void tryRecover(); }, 5000);
    return () => window.clearInterval(id);
  }, [degraded, tryRecover]);

  // Trip on any query that errors with a connectivity failure.
  useEffect(() => {
    const cache = qc.getQueryCache();
    return cache.subscribe((event) => {
      const q = (event as { query?: { state?: { status?: string; error?: unknown } } }).query;
      if (q?.state?.status === "error" && isConnectivityError(q.state.error)) setDegraded(true);
    });
  }, [qc]);

  // Trip on the browser losing the network; recover when it returns.
  useEffect(() => {
    const onOffline = () => setDegraded(true);
    const onOnline = () => { void tryRecover(); };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [tryRecover]);

  if (!degraded) return null;
  return (
    <div className="fixed inset-x-0 top-0 z-[200] flex items-center justify-center gap-2 bg-amber-500 px-4 py-1.5 text-center text-xs font-medium text-white shadow-md">
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      <span>Reconnecting to Pegasus — your connection dropped. Your work is safe; this clears itself the moment it's back.</span>
      <button
        type="button"
        onClick={() => void tryRecover()}
        className="ml-2 inline-flex shrink-0 items-center gap-1 rounded bg-white/20 px-2 py-0.5 font-semibold transition-colors hover:bg-white/30"
      >
        <RefreshCw className="h-3 w-3" /> Retry now
      </button>
    </div>
  );
}
